import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAdmin } from "@/context/AdminContext";
import { toast } from "sonner";
import ThemeToggle from "@/components/ThemeToggle";

/**
 * AdminLogin · Iter-191 · Theme-aware (light / dark / system).
 *
 * Colours flow from CSS variables (`--background`, `--foreground`, `--card`,
 * `--border`, `--accent`) so switching the ThemeToggle in the corner flips
 * the entire form — no white-on-white, no hardcoded palette.
 * Chrome autofill is handled globally in index.css.
 */
export default function AdminLogin() {
  const { admin, login } = useAdmin();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();

  if (admin) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await login(email, password);
      toast.success("Signed in");
      nav("/admin/dashboard");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Login failed";
      setErr(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center px-4 py-8 bg-background text-foreground">
      <div className="absolute top-4 right-4">
        <ThemeToggle variant="compact" />
      </div>
      <div className="w-full max-w-sm">
        <div className="text-center mb-5">
          <div className="text-2xl font-bold text-foreground mb-1">Zenith 360 · Admin</div>
          <div className="text-xs text-foreground/60">Platform-ops sign-in</div>
        </div>
        <form onSubmit={submit}
          className="rounded-sm border border-border bg-card text-card-foreground p-6 shadow-sm space-y-4"
          data-testid="admin-login-form">
          <div>
            <label className="block text-xs font-semibold mb-1.5 text-foreground/85">Email</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)}
              type="email" required autoFocus data-testid="admin-login-email"
              autoComplete="email"
              className="w-full h-10 px-3 rounded-sm border border-input bg-background text-foreground text-sm outline-none focus:border-accent focus:ring-1 focus:ring-accent/40 transition-colors" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1.5 text-foreground/85">Password</label>
            <input value={password} onChange={(e) => setPassword(e.target.value)}
              type="password" required data-testid="admin-login-password"
              autoComplete="current-password"
              className="w-full h-10 px-3 rounded-sm border border-input bg-background text-foreground text-sm outline-none focus:border-accent focus:ring-1 focus:ring-accent/40 transition-colors" />
          </div>
          {err && (
            <div data-testid="admin-login-error"
              className="rounded-sm border border-destructive/40 bg-destructive/10 text-destructive text-[13px] px-3 py-2">
              <b>Sign-in failed:</b> {err}
            </div>
          )}
          <button type="submit" disabled={busy} data-testid="admin-login-submit"
            className="w-full h-10 rounded-sm text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 transition-opacity">
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <div className="text-center">
            <a href="/forgot-password" data-testid="admin-login-forgot-link"
              className="text-xs text-accent hover:underline">
              Forgot password?
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}
